import React, { useMemo } from 'react';

import type { TPoint, TVertex } from '@vlabs/monokit/shared/types/regions';
import { Group, Line, Circle } from 'react-konva';

import { POLYGON_METRICS, FIRST_OUTLINE_PADDING, CLOSE_OUTLINE_PADDING, FILL_ALPHA_BASE, DEFAULT_INNER_COLOR } from './constants';
import { setAlphaChannelInColor, flatten } from './utils';

const dashCircle = [3, 3];
const dashLine = [6, 6];

export const DrawingOverlayPolygon: React.FC<{
  color: string;
  points: TVertex[];
  cursor?: TPoint | null;
  showCloseHint?: boolean;
  filled: boolean;
}> = ({ color, points, cursor = null, showCloseHint = false, filled }) => {
  const flattened = useMemo(() => flatten(points), [points]);
  const last = points[points.length - 1];
  const first = points[0];

  // массив координат для заливки-превью:
  // - обычно: уже поставленные точки + курсор
  // - если showCloseHint, показываем заливку замкнутого текущего контура (без курсора)
  let previewCoords = flattened;
  if (cursor && !showCloseHint) {
    previewCoords = [...flattened, cursor.x, cursor.y];
  }
  // заливка возможна, только если есть хотя бы 3 точки (6 чисел)
  const canFill = filled && previewCoords.length >= 6;

  const lineWidth = showCloseHint ? POLYGON_METRICS.strokeWidthHover : POLYGON_METRICS.strokeWidthDefault;
  return (
    <Group listening={false} name="drawing">
      {/* полупрозрачная заливка-превью будущего полигона */}
      {canFill && (
        <Line
          closed
          fill={setAlphaChannelInColor(color, FILL_ALPHA_BASE)}
          listening={false}
          points={previewCoords}
          stroke="transparent"
        />
      )}

      {/* уже поставленные отрезки */}
      {points.length > 1 && (
        <Line
          perfectDrawEnabled={false}
          points={flattened}
          shadowForStrokeEnabled={false}
          stroke={color}
          strokeScaleEnabled={false}
          strokeWidth={lineWidth}
        />
      )}

      {/* наводящая линия от last → cursor */}
      {last && cursor && (
        <Line
          dash={dashLine}
          perfectDrawEnabled={false}
          points={[last.x, last.y, cursor.x, cursor.y]}
          shadowForStrokeEnabled={false}
          stroke={color}
          strokeScaleEnabled={false}
          strokeWidth={lineWidth}
        />
      )}

      {points.map((p, idx) => (
        <Group key={p.id} listening={false}>
          {/* обводка у первой точки всегда */}
          {idx === 0 && (
            <Circle
              radius={POLYGON_METRICS.vertexRadiusPx + FIRST_OUTLINE_PADDING}
              stroke={color}
              strokeWidth={POLYGON_METRICS.strokeWidthHover}
              x={p.x}
              y={p.y}
            />
          )}
          {/* сама вершина */}
          <Circle
            fill={(idx === points.length - 1) ? color : DEFAULT_INNER_COLOR}
            radius={POLYGON_METRICS.vertexRadiusPx - 1}
            stroke={color}
            strokeWidth={POLYGON_METRICS.strokeWidthHover}
            x={p.x}
            y={p.y}
          />
        </Group>
      ))}

      {/* подсветка «готово к замыканию» — кольцо поверх первой точки */}
      {first && showCloseHint && (
        <Circle
          dash={dashCircle}
          listening={false}
          radius={POLYGON_METRICS.vertexRadiusPx + CLOSE_OUTLINE_PADDING}
          stroke={color}
          strokeWidth={POLYGON_METRICS.strokeWidthHover}
          x={first.x}
          y={first.y}
        />
      )}
    </Group>
  );
};
